import React from 'react';
import { useAgreementTemplatePreviewQuery, useCollaboratePhysicianMatchByIdForProviderQuery, useProviderDetailsQuery, useStateQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { Table, TableBody, TableRow, Typography } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { AgreementText, agreementTermTypeLabelMap, DetailsCell, DocumentLayout, ReviewAndSignAction, useSignatureAnchor } from './components';
import { SignAgreementCard } from './SignAgreementCard';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { CollaborativeAgreementExpired } from '@uplift/api';


export const CollaborativeAgreementExpiredPreview: React.VFC<{
  document: CollaborativeAgreementExpired;
  onSign: (agreementId: string) => void;
}> = ({
  document,
  onSign,
}) => {
  const me = useCurrentuser();
  const { data: match } = useCollaboratePhysicianMatchByIdForProviderQuery(me.id, document.collaborativeMatchId);
  const { data: preview } = useAgreementTemplatePreviewQuery(document.agreementTemplateId);
  const { data: state } = useStateQuery(match.stateId, { suspense: true });
  const { data: physicianName } = useUserDisplayNameQuery(match.collaborativePhysicianId, { suspense: true });
  const { data: details } = useProviderDetailsQuery(match.providerId, { suspense: true });
  const expiredAt = CalendarDateService.date(document.expiredAt, DateFormats.ISO_DATE);
  const { signatureRef, scrollToSignature } = useSignatureAnchor();

  return (
    <DocumentLayout
      title={preview.template.name}
      subtitle="Sent by BetterHelp"
      action={<ReviewAndSignAction onClick={scrollToSignature} />}
    >
      <Typography variant="overline" color="secondary">AGREEMENT DETAILS</Typography>
      <Table size="small" sx={{ 'tr:last-of-type td': { borderBottom: 0 } }}>
        <TableBody>
          <TableRow>
            <DetailsCell label="State Covered">{state.fullName}</DetailsCell>
            <DetailsCell label="Status">
              <Typography color="error" fontWeight={500}>
                Expired at {expiredAt.format(DateFormats['MMM D, YYYY'])}
              </Typography>
            </DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Effective Date">Upon counter-signature</DetailsCell>
            <DetailsCell label="Expires">{preview.template.agreementTermValue} {agreementTermTypeLabelMap[preview.template.agreementTermType][preview.template.agreementTermValue === 1 ? 'single' : 'many']} from effective date</DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Collaborating Physician">{physicianName.fullLegalName}</DetailsCell>
            <DetailsCell label="NPI">{details.npi ?? '-'}</DetailsCell>
          </TableRow>
        </TableBody>
      </Table>

      <AgreementText content={preview.version.content} />

      <SignAgreementCard ref={signatureRef} document={document} onSign={onSign} />
    </DocumentLayout>
  );
};
